import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { YearRow } from "@/lib/dividend-calc";

interface Props {
  rows: YearRow[];
  currency: string;
  labels: {
    year: string;
    contributed: string;
    portfolio: string;
    gross: string;
    net: string;
    cumulative: string;
    yoc: string;
  };
}

const ResultsTable = ({ rows, currency, labels }: Props) => {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const f = new Intl.NumberFormat(i18n.language, {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  });
  const pct = new Intl.NumberFormat(i18n.language, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });

  // Collapse long tables: show first 10 + last 3 with a toggle.
  const COLLAPSE_THRESHOLD = 15;
  const shouldCollapse = !expanded && rows.length > COLLAPSE_THRESHOLD;
  const visibleRows = shouldCollapse
    ? [...rows.slice(0, 10), null, ...rows.slice(-3)]
    : rows;

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr className="text-start">
              <th className="px-3 py-2 font-semibold text-start">{labels.year}</th>
              <th className="px-3 py-2 font-semibold text-start">{labels.contributed}</th>
              <th className="px-3 py-2 font-semibold text-start">{labels.portfolio}</th>
              <th className="px-3 py-2 font-semibold text-start">{labels.gross}</th>
              <th className="px-3 py-2 font-semibold text-start">{labels.net}</th>
              <th className="px-3 py-2 font-semibold text-start">{labels.cumulative}</th>
              <th className="px-3 py-2 font-semibold text-start">{labels.yoc}</th>
            </tr>
          </thead>
          <tbody>
            {visibleRows.map((r, i) =>
              r === null ? (
                <tr key={`gap-${i}`} className="border-t border-border/60 bg-muted/20">
                  <td colSpan={7} className="px-3 py-2 text-center text-muted-foreground text-xs">
                    …
                  </td>
                </tr>
              ) : (
                <tr key={r.year} className="border-t border-border/60">
                  <td className="px-3 py-2 tabular-nums">{r.year}</td>
                  <td className="px-3 py-2 tabular-nums">{f.format(r.contributed)}</td>
                  <td className="px-3 py-2 tabular-nums font-medium">{f.format(r.portfolioValue)}</td>
                  <td className="px-3 py-2 tabular-nums">{f.format(r.grossDividends)}</td>
                  <td className="px-3 py-2 tabular-nums text-primary">{f.format(r.netDividends)}</td>
                  <td className="px-3 py-2 tabular-nums">{f.format(r.cumulativeNetDividends)}</td>
                  <td className="px-3 py-2 tabular-nums">{pct.format(r.yieldOnCost)}%</td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
      {rows.length > COLLAPSE_THRESHOLD && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-sm font-semibold text-primary hover:underline"
        >
          {expanded ? t("calculator.cta.showLess") : t("calculator.cta.showAllYears", { count: rows.length })}
        </button>
      )}
    </div>
  );
};

export default ResultsTable;
